/**
 * AdminMediaCapsCard — admin sets per-role media library limits.
 * Four buckets: vendor / venue_provider / professional / entertainment_pro.
 * Each cap: max_images, max_videos, max_video_seconds.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ImagesSquare, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_LABELS = {
  vendor: 'Vendors (caterer, decorator, photographer-vendor, etc.)',
  venue_provider: 'Venue Providers',
  professional: 'Professionals (priest, decoration team, makeup, etc.)',
  entertainment_pro: 'Entertainment Pros (DJ, Nathaswaram)',
};

const AdminMediaCapsCard = () => {
  const [caps, setCaps] = useState({});
  const [defaults, setDefaults] = useState({});
  const [buckets, setBuckets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/media-caps`, { withCredentials: true });
      setCaps(data.caps || {});
      setDefaults(data.defaults || {});
      setBuckets(data.buckets || ['vendor', 'venue_provider', 'professional', 'entertainment_pro']);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load caps');
    }
    setLoading(false);
  };

  const update = (bucket, field, value) => {
    setCaps((cur) => ({
      ...cur,
      [bucket]: { ...(cur[bucket] || {}), [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { caps: {} };
      buckets.forEach((b) => {
        const c = caps[b] || {};
        payload.caps[b] = {
          max_images: Number(c.max_images) || 0,
          max_videos: Number(c.max_videos) || 0,
          max_video_seconds: Number(c.max_video_seconds) || 30,
        };
      });
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/media-caps`, payload, { withCredentials: true });
      setCaps(data.caps || {});
      toast.success('Media caps saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-media-caps-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImagesSquare size={20} className="text-brand-primary" weight="fill" /> Media Library Caps
        </CardTitle>
        <CardDescription>
          Per-role limits for the portfolio gallery (photos + ≤N-second videos). Customer-facing AI moderation
          rejects any caption-text overlays regardless of cap. Setting <span className="font-mono">max_videos = 0</span>{' '}
          disables video uploads for that role.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider font-bold text-text-secondary px-1">
              <div className="col-span-6">Role</div>
              <div className="col-span-2 text-center">Max photos</div>
              <div className="col-span-2 text-center">Max videos</div>
              <div className="col-span-2 text-center">Max sec/video</div>
            </div>
            {buckets.map((b) => {
              const c = caps[b] || {};
              const d = defaults[b] || {};
              return (
                <div key={b} className="grid grid-cols-12 gap-2 items-center"
                  data-testid={`admin-caps-row-${b}`}>
                  <div className="col-span-6">
                    <div className="text-sm font-medium">{BUCKET_LABELS[b] || b}</div>
                    <div className="text-[10px] text-text-secondary">
                      defaults: {d.max_images} pics · {d.max_videos} vids · {d.max_video_seconds}s
                    </div>
                  </div>
                  <Input type="number" min={0} max={200}
                    value={c.max_images ?? ''}
                    onChange={(e) => update(b, 'max_images', e.target.value)}
                    className="col-span-2 h-9 rounded-none border-[#E8E1D5] text-center text-sm"
                    data-testid={`admin-caps-images-${b}`} />
                  <Input type="number" min={0} max={50}
                    value={c.max_videos ?? ''}
                    onChange={(e) => update(b, 'max_videos', e.target.value)}
                    className="col-span-2 h-9 rounded-none border-[#E8E1D5] text-center text-sm"
                    data-testid={`admin-caps-videos-${b}`} />
                  <Input type="number" min={5} max={300}
                    value={c.max_video_seconds ?? ''}
                    onChange={(e) => update(b, 'max_video_seconds', e.target.value)}
                    className="col-span-2 h-9 rounded-none border-[#E8E1D5] text-center text-sm"
                    data-testid={`admin-caps-vidsec-${b}`} />
                </div>
              );
            })}

            <div className="flex items-center justify-end pt-3">
              <Button onClick={save} disabled={saving}
                className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
                data-testid="admin-caps-save-btn">
                {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                Save Caps
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminMediaCapsCard;
